import { PhoneOutlined, QuestionCircleOutlined, UserAddOutlined, WhatsAppOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  FloatButton,
  Input,
  Popover,
  Row,
  Table,
  Tour,
  TourProps,
} from "antd";
import type { ColumnsType, TableProps } from "antd/lib/table";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SignedInContext } from "../AppEnv";
import { SignedInDoctor } from "../auth/interfaces";
import { ALMSG_TIMEOUT, DEL_SUCCESS, PATIENTS_PATH } from "../env";
import AddPatientModal from "./AddPatientModal";
import { Patient } from "./interfaces";
import { getAllPatients } from "./utils";
import "../App.css";
import Plusbutton from "../buttons/plusbutton";
import Column from "antd/es/table/Column";

interface PatientRow extends Patient {
  key: number;
}

const columns: ColumnsType<PatientRow> = [
  {
    title: "Id",
    dataIndex: "key",
    sorter: {
      compare: (a, b) => a.key - b.key,
      multiple: 1, // Consentire solo l'ordinamento ascendente e discendente
    },
    defaultSortOrder: "descend", // Imposta l'ordinamento discendente di default
  },
  {
    title: "Nome",
    dataIndex: "name",
    sorter: {
      compare: (a, b) => (a.name < b.name ? -1 : 1),
    },
  },
  {
    title: "Cognome",
    dataIndex: "surname",
    sorter: {
      compare: (a, b) => (a.surname < b.surname ? -1 : 1),
    },
  },
  {
    title: "Data di nascita",
    dataIndex: "birthDate",
    render: (text) => (
      <>{text != undefined ? new Date("" + text).toLocaleDateString() : "-"}</>
    ),
  },

  {
    title: "Contatto telefonico",
    dataIndex: "telephone",
 
    render: (telephone) => (
      <>
        {telephone}
        <Button
          type="link"
          href={`tel:${telephone}`}
          icon={<PhoneOutlined rev={undefined} />}
          
        />
        {telephone.startsWith("+393") && (
          <Button
            type="link"
            href={`https://wa.me/${telephone}`}
            target="_blank"
            icon={<WhatsAppOutlined rev={undefined} />}
          />
        )}
      </>
    ),
  },
  {
    title: "Elicoidali",
    dataIndex: "elicoidali",
    sorter: {
      compare: (a, b) => (a.elicoidali === b.elicoidali ? 0 : a.elicoidali ? -1 : 1),
    },
    render: (elicoidali) => (   
    <span
      style={{
        backgroundColor: elicoidali ? "#8fddbf" : "#ca6868",
        color: "white",
        borderRadius: "100px",
        padding: elicoidali ? "7px 12px" : "8px 8px",
   
      }}
    >
      {elicoidali ? "Si" : "No"}
    </span>),
  },
  {
    title: "Solette",
    dataIndex: "solette",
    sorter: {
      compare: (a, b) => (a.solette === b.solette ? 0 : a.solette ? -1 : 1),
    },
    render: (solette) => (   
    <span
      style={{
        backgroundColor: solette ? "#8fddbf" : "#ca6868",
        color: "white",
        borderRadius: "100px",
        padding: solette ? "7px 12px" : "8px 8px",
   
      }}
    >
      {solette ? "Si" : "No"}
    </span>),
  },
];

const columns2: ColumnsType<PatientRow> = [
  {

    title: "Id",
    dataIndex: "key",
    sorter: {
      compare: (a, b) => a.key - b.key,
      multiple: 1, // Consentire solo l'ordinamento ascendente e discendente
    },
    defaultSortOrder: "descend", // Imposta l'ordinamento discendente di default
  },
  {
    title: "Nome",
    dataIndex: "name",
    sorter: {
      compare: (a, b) => (a.name < b.name ? -1 : 1),
    },
  },
  {
    title: "Cognome",
    dataIndex: "surname",
    sorter: {
      compare: (a, b) => (a.surname < b.surname ? -1 : 1),
      
    },
  },
 
 
];

const PatientsRegistry = () => {
  const navigate = useNavigate();
  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    //mount
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    //unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<PatientRow[]>();
  const [addPatientVisible, setAddPatientVisible] = useState(false);

  //add by leo
  const newPatient: JSX.Element = (
    <>
      <button className="traspiz" onClick={() => setAddPatientVisible(true)}>
        <Plusbutton />
      </button>
      <AddPatientModal
        visible={addPatientVisible}
        setVisible={setAddPatientVisible}
      />
    </>
  );

  //Leo Tutorial

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);
  const ref6 = useRef(null);
  const ref7 = useRef(null);

  const [open, setOpen] = useState<boolean>(false);

  const steps: TourProps["steps"] = [
    {
      title: "Aggiungi Nuovo Paziente",
      description:
        "Cliccando su questo pulsante, potrai creare una nuova scheda paziente.",
      target: () => ref1.current,
    },
    {
      title: "Ricerca Pazienti",
      description: "Qui potrai fare una ricerca nominale tra i tuoi pazienti.",
      target: () => ref2.current,
    },
    {
      title: " ID Paziente",
      description:
        "Alla creazione di ogni paziente gli verrà assegnato un ID; qui potrai filtrare in modo ascendente o discendente.",
      target: () => ref3.current,
    },
    {
      title: "Nome Paziente",
      description:
        "Qui potrai filtrare in modo ascendente o discendente con il nome paziente.",
      target: () => ref4.current,
    },
    {
      title: "Cognome Paziente",
      description:
        "Qui potrai filtrare in modo ascendente o discendente con il cognome paziente.",
      target: () => ref5.current,
    },
    {
      title: "Data Di Nascita",
      description:
        "In questa colonna verrà inidicata la data di nascita del paziente.",

      target: () => ref6.current,
    },
    {
      title: "Contatto Telefonico",
      description:
        "In questa colonna verrà inidicato il contatto telefonico del paziente.",

      target: () => ref7.current,
    },
  ];

  const contentx = (
    <>
      <Row>
        <Button onClick={() => setOpen(true)}>Tour Guidato</Button>

        <Button href="https://www.physiocheck.it/richiesta-assistenza/">
          Contattaci
        </Button>
      </Row>
    </>
  );

  const search = useLocation().search;
  let delSuccess: boolean =
    parseInt(new URLSearchParams(search).get(DEL_SUCCESS) || "0") == 1;

  useEffect(() => {
    if (delSuccess) setTimeout(() => (delSuccess = false), ALMSG_TIMEOUT);
  }, []);

  useEffect(() => {
    getAllPatients(signedInDoctor).then((fetchedPatients) => {
      if (fetchedPatients.length > 0) {
        setPatients(fetchedPatients);
        setFilteredPatients(
          fetchedPatients.map((p: Patient) => ({ ...p, key: p.id || -1 }))
        );
      }
    });
  }, [signedInDoctor]);

  return (
    <>
      <Tour open={open} onClose={() => setOpen(false)} steps={steps} />
      <div className="centered">
        <h3 className="section-title">{}</h3>
      </div>
      <div
        style={{
          marginLeft: "5%",
          marginRight: "5%",
          marginBottom: "16px",
        }}
      >
        <Row>
          <Col ref={ref1} xs={12} sm={10} md={8} lg={8} xl={12}>
            {newPatient}
          </Col>

          <Col ref={ref2} xs={12} sm={10} md={8} lg={8} xl={12}>
            <Input
              className="down1"
              placeholder="Cerca"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const valueLC: string = e.target.value.toLowerCase();
                setFilteredPatients(
                  patients
                    .filter(
                      (p: Patient) =>
                        p.name.toLowerCase().indexOf(valueLC) >= 0 ||
                        p.surname.toLowerCase().indexOf(valueLC) >= 0
                    )
                    .map((p: Patient) => ({ ...p, key: p.id || -1 }))
                );
              }}
            />
          </Col>
        </Row>
        <Popover
          placement="leftBottom"
          title="Hai Bisogno Di Aiuto?"
          content={contentx}
        >
          <FloatButton
            icon={<QuestionCircleOutlined rev={undefined} />}
            type="default"
            style={{ right: 24 }}
          />
        </Popover>
      </div>
      {delSuccess && (
        <div
          style={{
            marginLeft: "5%",
            marginRight: "5%",
            marginBottom: "16px",
          }}
        >
          {delSuccess}
          <Alert
            message="Operazione riuscita"
            description="Ogni dato relativo al paziente selezionato e' stato rimosso"
            type="success"
            showIcon
          />
        </div>
      )}
      {width > 640 && (
        <Table
          className="patient-table"
          columns={columns}
          dataSource={filteredPatients}
          style={{ marginLeft: "5%", marginRight: "5%" }}
          pagination={{ pageSize: 10 }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) =>
                navigate("/app" + PATIENTS_PATH + "/" + record.id), // click row
            };
          }}
        />
      )}
      {width < 640 && (
        <Table
          className="patient-table"
          columns={columns2}
          dataSource={filteredPatients}
          style={{ marginLeft: "5%", marginRight: "5%" }}
          pagination={{ pageSize: 9 }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) =>
                navigate("/app" + PATIENTS_PATH + "/" + record.id), // click row
            };
          }}
        />
      )}
    </>
  );
};

export default PatientsRegistry;
